import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
  requestScrollLock,
  requestScrollUnlock,
  scheduleAnimationCallback,
  withDelayedClose,
} from '@homebase/common';

// Active modals open at once
let modalKey = 0;
let primaryModalKey = null;
const modals = {};

const setPrimaryModalKey = () => {
  const firstKey = Object.keys(modals)[0];
  if (firstKey) {
    primaryModalKey = parseInt(firstKey, 10);
    const primaryInstance = modals[primaryModalKey];
    if (primaryInstance) {
      // @NOTE since we are tracking open modals outside of React state/props
      // we need to forceUpdate to get the overlay to properly display
      primaryInstance.forceUpdate();
    }
  }
};

const addOverlay = (inst) => {
  if (inst.__MODAL_MANAGER_ID__ == null) {
    inst.__MODAL_MANAGER_ID__ = modalKey++;
  }

  modals[inst.__MODAL_MANAGER_ID__] = inst;
  setPrimaryModalKey();
};

const removeOverlay = (inst) => {
  if (inst.__MODAL_MANAGER_ID__ != null) {
    delete modals[inst.__MODAL_MANAGER_ID__];
    setPrimaryModalKey();
  }
};

const withModalBasics = (WrappedComponent) => {
  class WithModalBasics extends Component {
    static propTypes = {
      escapeClose: PropTypes.bool,
      isOpen: PropTypes.bool.isRequired,
      scrollLock: PropTypes.bool,
      onRequestClose: PropTypes.func,
      overlayClose: PropTypes.bool,
      // If false, this disables the focus trapping mechanism
      enableFocusTrapping: PropTypes.bool,
      // pass true if needed for overlay on top of full page modal
      hideVeil: PropTypes.bool,
      requestScrollLock: PropTypes.func,
      requestScrollUnlock: PropTypes.func,
    };

    static defaultProps = {
      escapeClose: true,
      enableFocusTrapping: true,
      overlayClose: true,
      scrollLock: false,
      hideVeil: false,
      requestScrollLock,
      requestScrollUnlock,
      ...WrappedComponent.defaultProps,
    };

    componentDidMount() {
      if (this.props.escapeClose || this.props.enableFocusTrapping) {
        document.addEventListener('keydown', this.handleKeyDown);
      }
      if (this.props.isOpen) {
        // Don't add overlay if hideVeil was passed as true.
        if (!this.props.hideVeil) {
          addOverlay(this);
        }
        if (this.props.scrollLock) {
          // Delaying the call to scroll lock because we haven't re-rendered yet
          // to assign the ref for the modalElement
          scheduleAnimationCallback(() => {
            this.props.requestScrollLock(this.getScrollableElements());
          });
        }
      }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
      if (!this.props.isOpen && nextProps.isOpen) {
        if (!this.props.hideVeil) {
          addOverlay(this);
        }
        if (this.props.scrollLock) {
          // Delaying the call to scroll lock because we haven't re-rendered yet
          // to assign the ref for the modalElement
          scheduleAnimationCallback(() => {
            this.props.requestScrollLock(this.getScrollableElements());
          });
        }
      } else if (this.props.isOpen && !nextProps.isOpen) {
        if (!this.props.hideVeil) {
          removeOverlay(this);
        }
        if (this.props.scrollLock) {
          this.props.requestScrollUnlock(this.getScrollableElements());
        }
      } else if (
        this.props.isOpen &&
        nextProps.scrollLock !== this.props.scrollLock
      ) {
        // scroll lock setting of an active element is attempting to change
        if (!nextProps.scrollLock) {
          this.props.requestScrollUnlock(this.getScrollableElements());
        } else {
          this.props.requestScrollLock(this.getScrollableElements());
        }
      }
    }

    componentDidUpdate(prevProps) {
      if (this.props.isOpen && !prevProps.isOpen) {
        // save reference of previous focused element, then focus modal when it is opened
        this.previousActiveElement = document.activeElement;

        // We want to improve rendering performance since element.focus forces layout/reflow
        // https://gist.github.com/paulirish/5d52fb081b3570c81e3a
        scheduleAnimationCallback(() => {
          if (this.modalElement && this.modalElement.focus) {
            this.modalElement.focus();
          }
        });
      } else if (!this.props.isOpen && prevProps.isOpen) {
        // set focus to the element previously focused before the modal was opened, when the modal is closed
        scheduleAnimationCallback(() => {
          if (this.previousActiveElement && this.previousActiveElement.focus) {
            this.previousActiveElement.focus();
          }
        });
      }
    }

    componentWillUnmount() {
      if (this.props.isOpen && !this.props.hideVeil) {
        removeOverlay(this);
      }

      if (this.props.escapeClose || this.props.enableFocusTrapping) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
      // In the event a scrollLocked modal is removed from the component tree in a way other than toggling isOpen, cleanup the no-scroll class
      if (this.props.scrollLock && this.props.isOpen) {
        this.props.requestScrollUnlock(this.getScrollableElements());
      }
    }

    modalScrollElement = null;

    getScrollableElements = () => {
      // modals and drawers have different internal scrolling structures
      // We fork here on the correct scrolling container to ensure that they
      // are scroll locked correctly.
      // We return an array of elements because our scroll lock
      // library supports locking multiple elements at a time
      if (this.modalScrollElement !== null) {
        return [this.modalScrollElement];
      } else {
        return [this.modalElement];
      }
    };

    handleKeyDown = (e) => {
      if (!this.props.isOpen) {
        // exit early if the modal isn't open
        return;
      }
      switch (e.key) {
        case 'Tab':
          this.handleTab(e);
          break;
        case 'Escape':
          // check escapeClose since we still bind handleKeyDown even when it's false
          if (this.props.escapeClose) {
            this.props.onRequestClose(e);
          }
          break;
      }
    };

    overlayClick = (e) => {
      if (this.props.overlayClose) {
        this.props.onRequestClose(e);
      }
    };

    setModalElementRef = (ref) => {
      this.modalElement = ref;
    };

    setModalScrollElementRef = (ref) => {
      this.modalScrollElement = ref;
    };

    setLastTabbableRef = (ref) => {
      this.lastTabbable = ref;
    };

    // Used to determine if we should draw focus back to the previousActiveElement
    shouldReturnFocus = () =>
      this.props.hideVeil &&
      this.previousActiveElement &&
      typeof this.previousActiveElement.focus === 'function';

    handleTab = (event) => {
      if (event.target === this.lastTabbable && !event.shiftKey) {
        event.stopPropagation();
        event.preventDefault();

        // We want to improve rendering performance since element.focus forces layout/reflow
        // https://gist.github.com/paulirish/5d52fb081b3570c81e3a
        scheduleAnimationCallback(() => {
          // If we are hiding the veil, and a user tabs once more they should be brought back out to
          // where they interacted to open the drawer/modal
          if (this.shouldReturnFocus()) {
            this.previousActiveElement.focus();
          } else if (this.modalElement && this.modalElement.focus) {
            this.modalElement.focus();
          }
        });
      } else if (event.target === this.modalElement && event.shiftKey) {
        event.stopPropagation();
        event.preventDefault();
        scheduleAnimationCallback(() => {
          // If we are hiding the veil, and a user tabs once more they should be brought back out to
          // where they interacted to open the drawer/modal
          if (this.shouldReturnFocus()) {
            this.previousActiveElement.focus();
          } else if (this.lastTabbable && this.lastTabbable.focus) {
            this.lastTabbable.focus();
          }
        });
      } else if (
        // If they are on the previousActiveElement and the veil is hidden
        // and they are shift-tabbing then focus the last tabbable element
        // on the modal or drawer (the veil)
        this.props.hideVeil &&
        event.target === this.previousActiveElement &&
        event.shiftKey
      ) {
        event.stopPropagation();
        event.preventDefault();

        scheduleAnimationCallback(() => {
          if (
            this.lastTabbable &&
            typeof this.lastTabbable.focus === 'function'
          ) {
            this.lastTabbable.focus();
          }
        });
      }
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          setRef={this.setModalElementRef}
          setScrollableRef={this.setModalScrollElementRef}
          overlayClick={this.overlayClick}
          hideVeil={this.props.hideVeil}
          shouldShowOverlay={
            this.__MODAL_MANAGER_ID__ === primaryModalKey &&
            !this.props.hideVeil
          }
          setLastTabbableRef={this.setLastTabbableRef}
          isDialogModal={this.props.enableFocusTrapping}
        />
      );
    }
  }

  return withDelayedClose(WithModalBasics);
};

export default withModalBasics;
